import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Package, Plus, Edit2, Trash2, X, CheckCircle } from 'lucide-react';
import { BASE_URL } from '../config';
import { notify } from '../utils/toast';

const ACCENTS = [
  { borderColor: '#cbd5e1', checkColor: '#94a3b8', badgeBg: '#f1f5f9', badgeColor: '#475569' },
  { borderColor: '#f59e0b', checkColor: '#f59e0b', badgeBg: '#fef3c7', badgeColor: '#92400e', featured: true },
  { borderColor: '#3b82f6', checkColor: '#3b82f6', badgeBg: '#dbeafe', badgeColor: '#1d4ed8' },
  { borderColor: '#8b5cf6', checkColor: '#8b5cf6', badgeBg: '#ede9fe', badgeColor: '#6d28d9' },
  { borderColor: '#ef4444', checkColor: '#ef4444', badgeBg: '#fee2e2', badgeColor: '#b91c1c' },
  { borderColor: '#10b981', checkColor: '#10b981', badgeBg: '#d1fae5', badgeColor: '#065f46' },
];

function parseFeatures(details) {
  if (!details || !details.trim()) return [];
  return details.split(/\r?\n/).map(f => f.trim()).filter(Boolean);
}

function billingBadge(days) {
  const d = Number(days);
  if (d >= 360) return 'Yearly';
  if (d >= 25)  return 'Monthly';
  return `${d} Days`;
}

/* ─── card ─────────────────────────────────────────────────────────────── */
function PackageCard({ pkg, index, onEdit, onDelete }) {
  const acc      = ACCENTS[index % ACCENTS.length];
  const features = parseFeatures(pkg.details);
  const badge    = billingBadge(pkg.days_limit);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="relative bg-white rounded-xl flex flex-col"
      style={{
        border:    `2px solid ${acc.borderColor}`,
        minWidth:  260,
        width:     260,
        height:    '100%',           /* fills the stretch container */
        boxShadow: acc.featured
          ? `0 0 0 3px ${acc.borderColor}55, 0 8px 32px ${acc.borderColor}33`
          : '0 1px 6px rgba(0,0,0,0.07)',
      }}
    >
      {/* Popular ribbon */}
      {acc.featured && (
        <div style={{
          position:'absolute', top:0, right:0,
          width:76, height:76, overflow:'hidden',
          borderTopRightRadius:10, pointerEvents:'none',
        }}>
          <div style={{
            position:'absolute', top:18, right:-20, width:90,
            background:'#f59e0b', color:'#fff',
            fontSize:9, fontWeight:700, letterSpacing:'0.12em',
            textAlign:'center', padding:'3px 0',
            transform:'rotate(45deg)',
          }}>POPULAR</div>
        </div>
      )}

      {/* ── name + badge ── */}
      <div className="px-5 pt-5 pb-3">
        <div className="flex items-start justify-between gap-2 mb-3">
          <h3 className="text-base font-bold text-slate-800 leading-tight">{pkg.package_name}</h3>
          <span
            className="shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full"
            style={{ background: acc.badgeBg, color: acc.badgeColor }}
          >{badge}</span>
        </div>

        {/* price */}
        <div className="flex items-baseline gap-0.5">
          <span className="text-sm text-slate-400">₹</span>
          <span className="text-3xl font-extrabold text-slate-900 tracking-tight">
            {Number(pkg.price).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
          </span>
          <span className="text-xs text-slate-400 ml-1">/{badge === 'Yearly' ? 'year' : 'month'}</span>
        </div>
      </div>

      <hr className="mx-5 border-slate-100" />

      {/* ── features — flex-1 so it stretches ── */}
      <div className="px-5 py-4 flex-1 overflow-y-auto">
        {features.length > 0 ? (
          <>
            <p className="text-[9px] font-bold tracking-[0.15em] uppercase text-slate-400 mb-3">Features</p>
            <ul className="space-y-2">
              {features.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-slate-600">
                  <CheckCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: acc.checkColor }} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p className="text-xs text-slate-400 italic">No features added yet</p>
        )}
      </div>

      <hr className="mx-5 border-slate-100" />

      {/* ── stats ── */}
      <div className="px-5 py-3 space-y-2 text-xs">
        {[
          ['Credit Limit', Number(pkg.credit_limit) === 0 ? 'Unlimited' : `${Number(pkg.credit_limit).toLocaleString()} credits`],
          ['Storage',      pkg.storage ? `${pkg.storage} GB` : '—'],
          ['Duration',     `${pkg.days_limit} days`],
        ].map(([label, val]) => (
          <div key={label} className="flex justify-between items-center">
            <span className="text-slate-500">{label}</span>
            <span className="font-semibold text-slate-800">{val}</span>
          </div>
        ))}
      </div>

      <hr className="mx-5 border-slate-100" />

      {/* ── actions ── */}
      <div className="px-5 py-4 flex gap-2">
        <button
          onClick={() => onEdit(pkg)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium
                     border border-slate-200 text-slate-600 rounded-lg
                     hover:bg-slate-50 transition-colors"
        >
          <Edit2 className="w-3.5 h-3.5" /> Edit
        </button>
        <button
          onClick={() => onDelete(pkg.package_ID)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium
                     border border-red-200 text-red-500 rounded-lg
                     hover:bg-red-50 transition-colors"
        >
          <Trash2 className="w-3.5 h-3.5" /> Delete
        </button>
      </div>
    </motion.div>
  );
}

/* ─── main ─────────────────────────────────────────────────────────────── */
export default function PackageAdmin() {
  const [packages, setPackages]             = useState([]);
  const [showModal, setShowModal]           = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [loading, setLoading]               = useState(true);
  const [deleteId, setDeleteId]             = useState(null); // id pending delete confirm
  const [form, setForm] = useState({
    package_name:'', price:'', credit_limit:'',
    days_limit:'',   details:'', storage:'',
  });

  useEffect(() => { fetchPackages(); }, []);

  const fetchPackages = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/packages/all/`);
      setPackages(res.data);
    } catch { notify.error('Failed to load packages'); }
    finally  { setLoading(false); }
  };

  const emptyForm = () => ({
    package_name:'', price:'', credit_limit:'',
    days_limit:'',   details:'', storage:'',
  });

  const openCreate  = () => { setEditingPackage(null); setForm(emptyForm()); setShowModal(true); };
  const handleEdit  = (pkg) => {
    setEditingPackage(pkg);
    setForm({
      package_name: pkg.package_name, price:        pkg.price,
      credit_limit: pkg.credit_limit, days_limit:   pkg.days_limit,
      details:      pkg.details  || '',  storage: pkg.storage || '',
    });
    setShowModal(true);
  };
  const handleDelete = async (id) => {
    setDeleteId(id);
  };
  const confirmDelete = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      await axios.delete(`${BASE_URL}/api/packages/delete/${deleteId}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      notify.success('Package deleted'); fetchPackages();
    } catch { notify.error('Failed to delete package'); }
    finally { setDeleteId(null); }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('accessToken');
      if (editingPackage) {
        await axios.put(`${BASE_URL}/api/packages/update/${editingPackage.package_ID}/`, form,
          { headers: { Authorization: `Bearer ${token}` } });
        notify.success('Package updated');
      } else {
        await axios.post(`${BASE_URL}/api/packages/create/`, form,
          { headers: { Authorization: `Bearer ${token}` } });
        notify.success('Package created');
      }
      setShowModal(false); setEditingPackage(null); setForm(emptyForm()); fetchPackages();
    } catch { notify.error('Failed to save package'); }
  };

  return (
    /*
      The outer wrapper uses fixed positioning relative to the viewport.
      ml-64 accounts for the sidebar, and we leave p-6 top/bottom padding
      for the header. The cards area fills the remaining height.
    */
    <div style={{ display:'flex', flexDirection:'column', height:'calc(100vh - 76px)' }}>

      {/* ── Header ── */}
      <div className="flex items-center justify-between border-b border-slate-200 pb-4 mb-5 flex-shrink-0">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Package Management</h2>
          <p className="text-sm text-slate-500">Create and manage subscription packages</p>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-slate-500">
            <span className="text-xl font-bold text-slate-900">{packages.length}</span> packages
          </span>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600
                       text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" /> Create Package
          </button>
        </div>
      </div>

      {/* ── Cards — fills remaining height, scrolls horizontally ── */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : packages.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-slate-400">
          <Package className="w-14 h-14 mb-3" />
          <p className="text-sm">No packages yet. Create one!</p>
        </div>
      ) : (
        /*
          overflow-x-auto  → horizontal scroll
          flex-1           → takes all remaining vertical space
          The inner div uses align-items:stretch + height:100%
          so every card is as tall as the scroll area.
        */
        <div
          className="overflow-x-auto flex-1 flex items-center"
          style={{ minHeight: 0 }}
        >
          <div
            className="flex gap-5 py-4"
            style={{ minWidth: 'max-content', alignItems: 'stretch', height: '75%' }}
          >
            {packages.map((pkg, i) => (
              <PackageCard
                key={pkg.package_ID}
                pkg={pkg}
                index={i}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Modal ── */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50"
            onMouseDown={e => { if (e.target === e.currentTarget) setShowModal(false); }}
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1,    opacity: 1 }}
              exit={{    scale: 0.96, opacity: 0 }}
              onMouseDown={e => e.stopPropagation()}
              className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto p-6"
            >
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-base font-bold text-slate-900">
                  {editingPackage ? 'Edit Package' : 'Create Package'}
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {[
                  ['Package Name', 'package_name', 'text',   true,  'e.g. Pro Plan',  undefined],
                  ['Price (₹)',    'price',        'number', true,  '0.00',           '0.01'],
                  ['Credit Limit','credit_limit', 'number', true,  '0 = Unlimited',  undefined],
                  ['Days Limit',  'days_limit',   'number', true,  '30',             undefined],
                  ['Storage (GB)','storage',      'number', false, 'Optional',       undefined],
                ].map(([label, key, type, req, ph, step]) => (
                  <div key={key}>
                    <label className="block text-xs font-medium text-slate-700 mb-1.5">
                      {label}{req && <span className="text-red-500 ml-0.5">*</span>}
                    </label>
                    <input
                      type={type} step={step} value={form[key]}
                      required={!!req} placeholder={ph}
                      onChange={e => setForm({ ...form, [key]: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm
                                 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none"
                    />
                  </div>
                ))}

                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1.5">
                    Features <span className="text-slate-400 font-normal">(one per line)</span>
                  </label>
                  <textarea
                    rows={5} value={form.details}
                    onChange={e => setForm({ ...form, details: e.target.value })}
                    placeholder={"50 AI Credits per month\nPriority Support\nUnlimited Storage"}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm
                               focus:ring-1 focus:ring-primary-500 focus:border-primary-500
                               outline-none resize-none"
                  />
                </div>

                <div className="flex gap-3 pt-1">
                  <button type="button" onClick={() => setShowModal(false)}
                    className="flex-1 py-2 border border-slate-300 text-slate-600 text-sm
                               font-medium rounded-lg hover:bg-slate-50 transition-colors">
                    Cancel
                  </button>
                  <button type="submit"
                    className="flex-1 py-2 bg-primary-500 hover:bg-primary-600 text-white
                               text-sm font-medium rounded-lg transition-colors">
                    {editingPackage ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* ── Delete confirm modal ── */}
      <AnimatePresence>
        {deleteId && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1,    opacity: 1 }}
              exit={{    scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6"
            >
              <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mx-auto mb-4">
                <Trash2 className="w-5 h-5 text-red-500" />
              </div>
              <h3 className="text-base font-bold text-slate-900 text-center mb-1">Delete Package?</h3>
              <p className="text-sm text-slate-500 text-center mb-6">
                This action cannot be undone. The package will be permanently removed.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteId(null)}
                  className="flex-1 py-2 border border-slate-300 text-slate-600 text-sm
                             font-medium rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 py-2 bg-red-500 hover:bg-red-600 text-white text-sm
                             font-medium rounded-lg transition-colors"
                >
                  Yes, Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
